<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useGgFollowup,
  useGgFollowupSelection,
} from 'dashboard/composables/useGgFollowup';
import { useGgSupportTickets } from 'dashboard/composables/useGgSupportTickets';
import { useGgSupportState } from 'dashboard/composables/useGgSupportState';
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import followupIcon from 'dashboard/assets/images/gg-followup.svg?url';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import GgSupportSelect from './GgSupportSelect.vue';
import GgSupportLinkDialog from './GgSupportLinkDialog.vue';
import GgSupportStateDialog from './GgSupportStateDialog.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  compact: { type: Boolean, default: false },
});
const { t, te } = useI18n();
const { open: openSupport } = useGgFollowup('support');
const { close: closeSelection } = useGgFollowupSelection();
const { enabled: followupEnabled, open: openFollowup } = useGgFollowup();
const {
  enabled,
  tickets,
  loaded,
  error,
  busy,
  linkingAvailable,
  statusActionsAvailable,
  load: loadTickets,
} = useGgSupportTickets(() => props.conversationId, { loadOnMount: false });
const popover = ref(null);
const selectedId = ref('');
const linkDialog = ref(false);
const selectedTicket = computed(() =>
  tickets.value.find(ticket => String(ticket.chamado_id) === selectedId.value)
);
const {
  current: currentState,
  operation: stateOperation,
  error: stateError,
  busy: stateBusy,
  pending: statePending,
  blocked: stateBlocked,
  noteFailed,
  dialog: stateDialog,
  intent: stateIntent,
  refresh: refreshState,
  open: openStateDialog,
  submit: submitState,
  resume: recoverState,
} = useGgSupportState(
  () => props.conversationId,
  () => selectedTicket.value?.chamado_id,
  statusActionsAvailable
);
const actionOptions = [
  { action: 'triage', icon: 'i-lucide-list-filter' },
  { action: 'start', icon: 'i-lucide-play' },
  { action: 'test', icon: 'i-lucide-flask-conical' },
  { action: 'pause', icon: 'i-lucide-pause' },
  { action: 'resume', icon: 'i-lucide-circle-play' },
  { action: 'complete', icon: 'i-lucide-circle-check' },
];
const availableActions = computed(() =>
  actionOptions.filter(item =>
    currentState.value?.allowed_actions.includes(item.action)
  )
);
const stateErrorLabel = computed(() => {
  const code = stateOperation.value?.last_error_code || stateError.value;
  if (code === 'RATE_LIMITED') return t('GG_SUPPORT.CONTEXT.RATE_LIMITED');
  const key = `GG_SUPPORT_STATE.ERRORS.${code}`;
  return code
    ? t(te(key) ? key : 'GG_SUPPORT_STATE.ERRORS.REQUEST_FAILED')
    : '';
});
const showMenu = async () => {
  const refresh = statusActionsAvailable.value;
  await loadTickets();
  if (refresh) await refreshState();
};
const launchStateCommand = (action, hide) => {
  hide();
  if (action === 'complete') {
    stateDialog.value = null;
    openSupport(props.conversationId, {
      supportTicketId: selectedTicket.value.chamado_id,
      closingSupport: true,
    });
  } else {
    closeSelection();
    openStateDialog(action);
  }
};
const triggerLabel = computed(() => t('GG_SUPPORT_HEADER.TITLE'));
const ticketLabel = ticket =>
  ticket.ticket_code ||
  t('GG_SUPPORT_HEADER.TICKET', { id: ticket.chamado_id });
const ticketOptions = computed(() =>
  tickets.value.map(ticket => ({
    value: String(ticket.chamado_id),
    label: ticketLabel(ticket),
  }))
);
const recordedStatus = computed(() => {
  const status = selectedTicket.value?.recorded_status;
  return status ? t(`GG_SUPPORT_HEADER.STATUS['${status}']`) : '';
});
const statusCommands = [
  { label: 'START', icon: 'i-lucide-play' },
  { label: 'PAUSE', icon: 'i-lucide-pause' },
  { label: 'RESUME', icon: 'i-lucide-circle-play' },
  { label: 'COMPLETE', icon: 'i-lucide-circle-check' },
];
watch(tickets, () => {
  if (!selectedTicket.value)
    selectedId.value = String(tickets.value[0]?.chamado_id || '');
});
const launchSupport = hide => {
  hide();
  openSupport(props.conversationId);
};
const launchLink = hide => {
  hide();
  closeSelection();
  linkDialog.value = true;
};
const linked = () => {
  linkDialog.value = false;
  loadTickets();
};
const launchFollowup = hide => {
  hide();
  openFollowup(props.conversationId, {
    supportTicketId: selectedTicket.value?.chamado_id || null,
  });
};
watch([() => props.conversationId, enabled], () => {
  popover.value?.hide();
  selectedId.value = '';
  linkDialog.value = false;
});
</script>

<template>
  <Popover v-if="enabled" ref="popover" :key="conversationId" @show="showMenu">
    <template #default="{ isOpen }">
      <ButtonGroup
        class="inline-flex shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
        @click.stop
      >
        <span
          :title="triggerLabel"
          class="inline-flex h-8 min-w-0 items-center rounded-s-lg bg-n-button-color text-sm text-n-slate-12 cursor-default"
          :class="compact ? 'w-8 justify-center' : 'gap-2 px-3'"
        >
          <Icon
            icon="i-lucide-cpu"
            class="size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span :class="compact ? 'sr-only' : 'truncate'">{{
            triggerLabel
          }}</span>
        </span>
        <Button
          :aria-label="t('GG_SUPPORT_HEADER.OPEN_MENU')"
          :aria-expanded="isOpen"
          aria-haspopup="dialog"
          icon="i-lucide-chevron-down"
          color="slate"
          size="sm"
          no-animation
          class="rounded-s-none !outline-0"
          @click="popover.toggle()"
        />
      </ButtonGroup>
    </template>
    <template #content="{ hide }">
      <div
        role="dialog"
        :aria-label="t('GG_SUPPORT_HEADER.MENU_TITLE')"
        class="w-full md:w-[19rem] max-w-full p-3 flex flex-col gap-2"
      >
        <div v-if="busy" class="flex justify-center py-3">
          <Spinner />
        </div>
        <template v-else-if="error">
          <p role="alert" class="m-0 text-xs text-n-ruby-11">
            {{ t('GG_SUPPORT_HEADER.ERROR') }}
          </p>
          <Button
            :label="t('GG_SUPPORT_HEADER.RETRY')"
            color="slate"
            variant="ghost"
            size="sm"
            @click="loadTickets"
          />
        </template>
        <template v-else-if="loaded">
          <GgSupportSelect
            v-if="tickets.length > 1"
            id="gg-support-header-ticket"
            v-model="selectedId"
            :label="t('GG_SUPPORT_HEADER.SELECT_TICKET')"
            :options="ticketOptions"
          />
          <p v-else-if="selectedTicket" class="m-0 text-sm text-n-slate-12">
            {{ ticketLabel(selectedTicket) }}
          </p>
          <p v-else class="m-0 text-xs text-n-slate-11">
            {{ t('GG_SUPPORT_HEADER.EMPTY') }}
          </p>
          <p
            v-if="recordedStatus && !statusActionsAvailable"
            class="m-0 text-xs text-n-slate-11"
          >
            {{
              t('GG_SUPPORT_HEADER.RECORDED_STATUS', { status: recordedStatus })
            }}
          </p>
          <template v-if="selectedTicket && statusActionsAvailable">
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ currentState?.ticket.state_label }}
              </span>
              <Button
                icon="i-lucide-refresh-cw"
                :aria-label="t('GG_SUPPORT_STATE.REFRESH')"
                :title="t('GG_SUPPORT_STATE.REFRESH')"
                :disabled="stateBusy"
                variant="ghost"
                color="slate"
                size="sm"
                @click="refreshState"
              />
            </div>
            <div
              v-if="stateBusy || statePending"
              class="flex items-center gap-2 text-xs text-n-slate-11"
            >
              <Spinner />
              {{
                t(
                  statePending
                    ? 'GG_SUPPORT_STATE.WAITING'
                    : 'GG_SUPPORT_STATE.LOADING'
                )
              }}
            </div>
            <p
              v-if="stateErrorLabel"
              role="alert"
              class="m-0 text-xs text-n-ruby-11"
            >
              {{ stateErrorLabel }}
            </p>
            <p
              v-if="stateOperation?.state === 'saved'"
              class="m-0 text-xs text-n-teal-11"
            >
              {{
                t('GG_SUPPORT_STATE.SAVED', {
                  code: stateOperation.result.ticket.ticket_code,
                })
              }}
            </p>
            <Button
              v-if="stateBlocked || noteFailed || stateIntent"
              :label="
                t(
                  noteFailed
                    ? 'GG_SUPPORT_STATE.RECOVER_NOTE'
                    : 'GG_SUPPORT_STATE.RECOVER'
                )
              "
              :disabled="stateBusy"
              variant="faded"
              color="slate"
              size="sm"
              @click="recoverState"
            />
            <div
              v-if="currentState"
              class="border-t border-n-weak pt-2 flex flex-col"
            >
              <Button
                v-for="command in availableActions"
                :key="command.action"
                :icon="command.icon"
                :label="t(`GG_SUPPORT_STATE.ACTIONS.${command.action}`)"
                :disabled="stateBusy || statePending || stateBlocked"
                variant="ghost"
                color="slate"
                size="sm"
                justify="start"
                class="w-full"
                @click="launchStateCommand(command.action, hide)"
              />
              <p
                v-if="!availableActions.length"
                class="m-0 text-xs text-n-slate-11"
              >
                {{ t('GG_SUPPORT_STATE.NO_ACTIONS') }}
              </p>
            </div>
          </template>
          <template v-else-if="selectedTicket">
            <div class="border-t border-n-weak pt-2 flex flex-col">
              <Button
                v-for="command in statusCommands"
                :key="command.label"
                :icon="command.icon"
                :label="t(`GG_SUPPORT_HEADER.COMMANDS.${command.label}`)"
                disabled
                variant="ghost"
                color="slate"
                size="sm"
                justify="start"
                class="w-full"
              />
            </div>
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('GG_SUPPORT_HEADER.STATUS_PENDING') }}
            </p>
          </template>
        </template>
        <div class="border-t border-n-weak pt-2 flex flex-col gap-1">
          <Button
            v-if="linkingAvailable"
            :label="t('GG_SUPPORT_LINKS.TITLE')"
            icon="i-lucide-link"
            variant="ghost"
            color="slate"
            size="sm"
            justify="start"
            class="w-full"
            @click="launchLink(hide)"
          />
          <Button
            :label="t('GG_SUPPORT_HEADER.NEW_TICKET')"
            icon="i-lucide-plus"
            variant="ghost"
            color="slate"
            size="sm"
            justify="start"
            class="w-full"
            @click="launchSupport(hide)"
          />
          <Button
            :label="t('GG_SUPPORT_HEADER.FOLLOWUP')"
            :disabled="!followupEnabled"
            variant="ghost"
            color="slate"
            size="sm"
            justify="start"
            class="w-full"
            @click="launchFollowup(hide)"
          >
            <template #icon>
              <img
                :src="followupIcon"
                class="size-4 shrink-0 guandalini:dark:rounded-sm guandalini:dark:bg-guandalini-warm"
                alt=""
                draggable="false"
              />
            </template>
          </Button>
        </div>
      </div>
    </template>
  </Popover>
  <GgSupportLinkDialog
    v-if="enabled && linkDialog"
    :conversation-id="conversationId"
    :tickets="tickets"
    @saved="linked"
    @close="linkDialog = false"
  />
  <GgSupportStateDialog
    v-if="stateDialog"
    :action="stateDialog.action"
    :snapshot="stateDialog.snapshot"
    :error="stateErrorLabel"
    @confirm="submitState"
    @close="stateDialog = null"
  />
</template>
